import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  CurrencyInr, ArrowsClockwise, Spinner, ChartBar,
  Lightning, ShieldCheck, User, ArrowUpRight, Buildings,
} from "@phosphor-icons/react";

const WINDOWS = [7, 14, 30, 60, 90];

export default function LLMSpend() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [openLogId, setOpenLogId] = useState(null);
  const [logDetail, setLogDetail] = useState(null);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/spend/summary?days=${d}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(days);
  }, [days]);

  useEffect(() => {
    if (!openLogId) {
      setLogDetail(null);
      return;
    }
    api.get(`/query-logs/${openLogId}`).then(({ data }) => setLogDetail(data)).catch(() => setLogDetail(null));
  }, [openLogId]);

  const peakDaily = useMemo(() => {
    if (!data?.daily?.length) return 0;
    return Math.max(...data.daily.map((d) => d.cost_inr));
  }, [data]);

  if (loading && !data) {
    return <div className="p-12 text-center mono-label pulse-soft">Loading spend telemetry…</div>;
  }
  if (!data) return null;

  const lifetime = data.totals.lifetime;
  const win = data.totals.window;
  const today = data.totals.today;
  const single = data.mode_breakdown.single;
  const agentic = data.mode_breakdown.agentic;

  return (
    <div className="space-y-8" data-testid="spend-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="mono-label text-orange-600 mb-2">// finops · llm cost telemetry</div>
          <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
            LLM Spend
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Daily ₹ burn, per-user attribution and the queries lighting your token bill on fire.
            All amounts include input + output, priced at <span className="font-mono">${data.pricing.input_usd_per_m}/M in</span> ·{" "}
            <span className="font-mono">${data.pricing.output_usd_per_m}/M out</span> · <span className="font-mono">₹{data.pricing.usd_to_inr}/USD</span>.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="spend-window-switch">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              data-testid={`spend-window-${w}`}
              className={`btn-sharp text-xs ${days === w ? "btn-primary" : "btn-secondary"}`}
            >
              {w}d
            </button>
          ))}
          <button
            onClick={() => load(days)}
            className="btn-sharp btn-ghost text-xs"
            data-testid="spend-refresh"
            title="Refresh"
          >
            {loading ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
          </button>
        </div>
      </div>

      {/* Top metric strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="spend-metrics">
        <Metric icon={CurrencyInr} label={`Spend · ${days}d`} value={`₹${win.cost_inr.toFixed(2)}`} sub={`${win.queries} queries`} tone="orange" />
        <Metric icon={CurrencyInr} label="Today" value={`₹${today.cost_inr.toFixed(4)}`} sub={`${today.queries} queries`} tone="emerald" />
        <Metric icon={CurrencyInr} label="Lifetime" value={`₹${lifetime.cost_inr.toFixed(2)}`} sub={`${lifetime.queries} queries`} />
        <Metric icon={ChartBar} label="Avg / query" value={`₹${(win.avg_cost_inr || 0).toFixed(4)}`} sub={`${(win.tokens / Math.max(1, win.queries)).toFixed(0)} tok avg`} />
        <Metric icon={Lightning} label="Tokens · window" value={fmtTokens(win.tokens)} sub="input + output" />
      </div>

      {/* Daily bar chart */}
      <section className="border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <ChartBar size={14} className="text-orange-600" />
          <div className="mono-label">Daily ₹ burn — last {days} days</div>
          <div className="ml-auto font-mono text-xs text-slate-500">
            peak ₹{peakDaily.toFixed(4)}
          </div>
        </div>
        <div className="p-6">
          {data.daily.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-8">No spend in this window.</div>
          ) : (
            <div className="flex items-end gap-1 h-48 relative" data-testid="spend-daily-chart">
              {data.daily.map((d) => {
                const h = peakDaily > 0 ? Math.max(2, (d.cost_inr / peakDaily) * 100) : 2;
                const agenticPct = d.queries > 0 ? (d.agentic / d.queries) * 100 : 0;
                return (
                  <div key={d.date} className="flex-1 h-full flex flex-col justify-end items-center group relative">
                    <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-slate-900 text-white text-xs px-2 py-1 font-mono whitespace-nowrap" style={{ borderRadius: 2 }}>
                      {d.date} · ₹{d.cost_inr.toFixed(4)} · {d.queries}q ({d.agentic}a/{d.single}s)
                    </div>
                    <div className="w-full relative bg-orange-500/20" style={{ height: `${h}%` }}>
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-orange-600"
                        style={{ height: `${agenticPct}%` }}
                        title={`${d.agentic} agentic`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-4 mt-4 mono-label">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-orange-600 inline-block" /> agentic</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-orange-500 opacity-20 inline-block" /> single-shot</span>
          </div>
        </div>
      </section>

      {/* Mode breakdown */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ModeCard label="Single-shot RAG" icon={Lightning} mode={single} total={single.cost_inr + agentic.cost_inr} testid="mode-single" />
        <ModeCard label="6-Agent Mesh" icon={ShieldCheck} mode={agentic} total={single.cost_inr + agentic.cost_inr} testid="mode-agentic" />
      </section>

      {/* Top users + Top queries */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Top users */}
        <div className="lg:col-span-2 border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <User size={14} className="text-orange-600" />
            <div className="mono-label">Top spenders · {days}d</div>
          </div>
          {data.top_users.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No usage yet.</div>
          ) : (
            <div data-testid="top-users">
              {data.top_users.map((u, i) => {
                const pct = win.cost_inr > 0 ? (u.cost_inr / win.cost_inr) * 100 : 0;
                return (
                  <div key={u.user_id} className="px-5 py-3 border-b border-slate-100 last:border-0" data-testid={`user-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs px-1.5 py-0.5 bg-slate-900 text-white w-6 text-center">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-900 truncate">{u.email}</div>
                        <div className="mono-label">
                          {u.role || "user"} · {u.queries} queries · {fmtTokens(u.tokens)} tok
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm text-orange-600 font-medium">₹{u.cost_inr.toFixed(4)}</div>
                        <div className="mono-label">{pct.toFixed(1)}%</div>
                      </div>
                    </div>
                    <div className="mt-2 h-1 bg-slate-100">
                      <div className="h-full bg-orange-600" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top queries */}
        <div className="lg:col-span-3 border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <CurrencyInr size={14} className="text-orange-600" />
            <div className="mono-label">Most expensive queries · {days}d</div>
          </div>
          {data.top_queries.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No queries yet.</div>
          ) : (
            <div data-testid="top-queries">
              {data.top_queries.map((q, i) => (
                <button
                  key={q.id}
                  onClick={() => setOpenLogId(q.id)}
                  className="w-full text-left px-5 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-all flex items-start gap-3"
                  data-testid={`query-row-${i}`}
                >
                  <span className="font-mono text-xs px-1.5 py-0.5 bg-slate-900 text-white shrink-0 w-6 text-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 line-clamp-2">{q.query}</div>
                    <div className="mono-label mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 ${q.mode === "agentic" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
                        {q.mode === "agentic" ? "MESH" : "SINGLE"}
                      </span>
                      <span>{q.user_email}</span>
                      <span>· {q.total_tokens || 0} tok</span>
                      <span>· {q.latency_ms || 0}ms</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm text-orange-600 font-medium">₹{q.cost_inr.toFixed(4)}</div>
                    <ArrowUpRight size={12} className="ml-auto text-slate-400" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail drawer */}
      {openLogId && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={() => setOpenLogId(null)} data-testid="spend-drawer">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-white h-full overflow-y-auto border-l border-slate-200"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="mono-label text-orange-600">// query detail</div>
                <div className="font-mono text-xs text-slate-500 mt-1">{openLogId}</div>
              </div>
              <button onClick={() => setOpenLogId(null)} className="btn-sharp btn-ghost" data-testid="spend-drawer-close">
                Close
              </button>
            </div>
            <div className="p-6 space-y-5">
              {!logDetail ? (
                <div className="text-center mono-label pulse-soft py-8">Loading…</div>
              ) : (
                <>
                  <div>
                    <div className="mono-label mb-2">Query</div>
                    <div className="border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-mono">{logDetail.query}</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <Stat label="Cost" value={`₹${Number(logDetail.cost_inr || 0).toFixed(4)}`} />
                    <Stat label="Tokens" value={logDetail.total_tokens || 0} />
                    <Stat label="Latency" value={`${logDetail.latency_ms || 0}ms`} />
                    <Stat label="Mode" value={(logDetail.mode || "single").toUpperCase()} />
                  </div>
                  <div>
                    <div className="mono-label mb-2">Answer</div>
                    <div className="border border-slate-200 px-4 py-3 text-sm whitespace-pre-wrap">{logDetail.answer || "—"}</div>
                  </div>
                  {(logDetail.citations || []).length > 0 && (
                    <div>
                      <div className="mono-label mb-2">Citations ({logDetail.citations.length})</div>
                      <div className="space-y-1">
                        {logDetail.citations.map((c, ci) => (
                          <div key={ci} className="border border-slate-200 px-3 py-2 bg-slate-50 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-mono px-1.5 py-0.5 bg-slate-900 text-white">[{ci + 1}]</span>
                              <Buildings size={12} className="text-slate-500" />
                              <span className="font-mono text-slate-700 truncate flex-1">{c.location_path}</span>
                              <span className="font-mono text-orange-600">score {Number(c.score).toFixed(2)}</span>
                            </div>
                            <div className="text-sm text-slate-900 font-medium">{c.title}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone = "slate" }) {
  const color =
    tone === "orange" ? "text-orange-700"
      : tone === "emerald" ? "text-emerald-700"
      : "text-slate-900";
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label flex items-center gap-1.5">
        <Icon size={12} />
        {label}
      </div>
      <div className={`font-display text-2xl mt-1 ${color}`}>{value}</div>
      {sub && <div className="mono-label text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function ModeCard({ label, icon: Icon, mode, total, testid }) {
  const pct = total > 0 ? (mode.cost_inr / total) * 100 : 0;
  return (
    <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-orange-600" />
        <div className="mono-label">{label}</div>
        <div className="ml-auto font-mono text-xs text-slate-500">{pct.toFixed(1)}% of spend</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="mono-label">Cost</div>
          <div className="font-display text-2xl text-orange-700">₹{mode.cost_inr.toFixed(2)}</div>
        </div>
        <div>
          <div className="mono-label">Queries</div>
          <div className="font-display text-2xl text-slate-900">{mode.queries}</div>
        </div>
        <div>
          <div className="mono-label">Tokens</div>
          <div className="font-display text-2xl text-slate-900">{fmtTokens(mode.tokens)}</div>
        </div>
      </div>
      <div className="mt-3 h-1 bg-slate-100">
        <div className="h-full bg-orange-600" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="mono-label">{label}</div>
      <div className="font-mono text-sm text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function fmtTokens(n) {
  n = Number(n) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}
